'use client';
import { useEffect, useState } from "react";
import Image from "next/image";
import { useLocation, useParams } from '@/lib/router';
import PageLayout from "../PageLayout";
import FAQAccordion from "./faq";
import { Trans, useTranslation } from "react-i18next";
import { defaultLang, SUPPORTED } from "../../config/languages";
import { LangLink } from "../../i18n/links";
import i18nInstance from "../../i18n";
import { coffee_shop_slug, gyms_slug, hospitals_slug, lobby_slug } from "../Solutions/solutions/slugs";
import CallToAction from "../CallToAction";
import CTA_2 from "../CTA_2";
import TVs from "../TVs";
import { openCookiePreferences } from '../../lib/cookieConsent';
import { getAffiliateCodeFromCurrentUrl, getAppDownloadUrl } from '../../lib/appDownloadLink';

const HERO_IMAGES = [
  "/assets/img/hero/hero.webp",
  "/assets/img/hero/hero2.webp",
];

const USE_CASE_CARDS = [
  { key: 'cafes', image: 'cafe-digital-signage.webp', icon: 'use-case-cafe.svg', slug: coffee_shop_slug },
  { key: 'salons', image: 'salon-digital-signage.webp', icon: 'use-case-salon.svg' },
  { key: 'bakeries', image: 'bakery-digital-signage.webp', icon: 'use-case-bakery.svg', slug: coffee_shop_slug },
  { key: 'gyms', image: 'gym-digital-signage.webp', icon: 'use-case-gym.svg', slug: gyms_slug },
  { key: 'vet_clinics', image: 'vet-digital-signage.webp', icon: 'use-case-vet.svg', slug: hospitals_slug },
  { key: 'real_estate', image: 'real-estate-digital-signage.webp', icon: 'use-case-real-estate.svg' },
  { key: 'waiting_rooms', image: 'waiting-room-digital-signage.webp', icon: 'use-case-clinic.svg', slug: hospitals_slug },
  { key: 'boutiques', image: 'retail-digital-signage.webp', icon: 'use-case-retail.svg' },
  { key: 'lobbies', image: 'lobby-digital-signage.webp', icon: 'use-case-lobby.svg', slug: lobby_slug },
];

const COMPARISON_ROWS = [
  { key: 1, icon: 'comparison-price.svg' },
  { key: 2, icon: 'comparison-laptop.svg' },
  { key: 3, icon: 'comparison-usb.svg' },
  { key: 4, icon: 'comparison-phone.svg' },
  { key: 5, icon: 'comparison-clock.svg' },
  { key: 7, icon: 'comparison-sparkles.svg' },
];

const Home = ({ layoutVersion = '2' }) => {
  const { hash } = useLocation();
  const { lang = defaultLang } = useParams();
  const activeLang = SUPPORTED.includes(lang) ? lang : defaultLang;
  const needsExtraHeroTop = ['de', 'fr', 'it', 'pt', 'sr'].includes(activeLang);
  const heroLangClass = needsExtraHeroTop ? ` home-hero-spacious home-hero-${activeLang}` : '';
  if (i18nInstance.language !== activeLang) {
    i18nInstance.changeLanguage(activeLang);
  }
  const { t } = useTranslation(['home', 'call_to_action', 'privacy', 'pricing', 'header']);
  const solSlug = (slug) => `/solutions/${typeof slug === 'string' ? slug : (slug[activeLang] || slug.en)}`;
  const [heroImageIndex, setHeroImageIndex] = useState(0);
  const [videoRequested, setVideoRequested] = useState(false);

  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 0);
      }
    }
  }, [hash]);

  useEffect(() => {
    if (layoutVersion !== '3') return undefined;
    document.body.classList.add('layout-3');
    return () => document.body.classList.remove('layout-3');
  }, [layoutVersion]);

  useEffect(() => {
    if (!['1', '2'].includes(layoutVersion) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }

    const root = document.documentElement;
    const revealTargets = document.querySelectorAll('[data-home-reveal]');
    let animationFrame;

    const updateParallax = () => {
      const distance = Math.min(window.scrollY, 1100);
      root.style.setProperty('--home-parallax-slow', `${Math.round(distance * 0.07)}px`);
      root.style.setProperty('--home-parallax-fast', `${Math.round(distance * 0.035)}px`);
      animationFrame = undefined;
    };

    const onScroll = () => {
      if (!animationFrame) animationFrame = window.requestAnimationFrame(updateParallax);
    };

    const observer = 'IntersectionObserver' in window
      ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-motion-visible');
            observer.unobserve(entry.target);
          });
        },
        { threshold: 0.14, rootMargin: '0px 0px -6%' }
      )
      : null;

    document.body.classList.add('home-motion');
    root.classList.add('home-motion-ready');
    if (observer) {
      revealTargets.forEach((target) => observer.observe(target));
    } else {
      revealTargets.forEach((target) => target.classList.add('is-motion-visible'));
    }
    updateParallax();
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScroll);
      observer?.disconnect();
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
      document.body.classList.remove('home-motion');
      root.classList.remove('home-motion-ready');
      root.style.removeProperty('--home-parallax-slow');
      root.style.removeProperty('--home-parallax-fast');
    };
  }, [layoutVersion]);

  useEffect(() => {
    if (!['1', '2'].includes(layoutVersion) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }

    const interval = window.setInterval(() => {
      setHeroImageIndex((current) => (current + 1) % HERO_IMAGES.length);
    }, 7000);

    return () => window.clearInterval(interval);
  }, [layoutVersion]);

    return (
      <>
      <PageLayout isHome={true} 
        title={t('home:pageTitle')}
        metaDescription={t('home:pageMetaDescription')}
        canonical={`https://www.adcast.app${activeLang === 'en' ? '' : '/' + activeLang}/`}
        keywords={t('home:keywords')}
      >

      <div className={`welcome_area primary_bg${heroLangClass}`}>
        <div className="container">
          <div className="row">
            <div className="col-lg-7 align-self-center home-hero-copy">
              <div className="title2">
                  <h1>
                    {t('mainTitle')} {t('subTitle')}
                  </h1>
                  <div className="space-20" />
                  <p className="hero-subtitle">
                    <Trans i18nKey="home:section_1" components={{ strong: <strong /> }} />
                  </p>

                  <div className="space-20" />
                  <p className="lead">
                    <Trans i18nKey="home:section_2" components={{ strong: <strong /> }} />
                  </p>

                  <div className="home-cta-row">
                    <div className="home-cta-unified">
                      <span className="home-cta-label">{t('pricing:redesign.trial_benefits_label')}</span>
                      <strong className="home-cta-headline">{t('pricing:redesign.hero_trial')}</strong>
                      <a className="home-cta-primary" href="https://workspace.adcast.app/auth/signup" target="_blank" rel="noopener noreferrer">
                        {t('header:get_started')} <span aria-hidden="true">→</span>
                      </a>
                      <p className="home-cta-footnote">
                        {t('pricing:redesign.trial_benefit_card')} · {t('pricing:redesign.trial_all_features_note')}
                      </p>
                    </div>

                    <div className="home-download-card">
                      <p className="home-download-card-title">{t('home:download_area.hero_card_title')}</p>
                      <CallToAction variant="mobile-card" />
                    </div>
                  </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="illas_box" data-home-reveal>
          <div className="illas_wrap">
            <Image
              key={HERO_IMAGES[heroImageIndex]}
              src={HERO_IMAGES[heroImageIndex]}
              alt={`${t('mainTitle')} ${t('subTitle')}`}
              className="img-fluid home-hero-cycle-image"
              width={580}
              height={690}
              sizes="(max-width: 767px) 290px, 580px"
              priority={heroImageIndex === 0}
            />
            {/* <img className="illas2" src="/assets/img/hero/hero311.png" alt=""> */}
          </div>
        </div>
      </div>



      {/* ==== PRICING BANNER ==== */}
      {/* <div className="text-center py-5 bg-light">
        <div className="container">
          {/* <h2>{t('simple_pricing_title')}</h2>
          <p className="display-6 fw-bold text-primary">
            <Trans
              i18nKey="home:pricing_line"
              components={[
                <sup />,                         // <0> ... </0>
                <small className="fs-3" />,      // <1> ... </1>
                <sup />,                         // <2> ... </2>
                <small className="fs-3" />       // <3> ... </3>
              ]}
            />
          </p>
          <p className="lead">{t('pricing_features')}</p>
          <a href="#download" className="btn btn-primary btn-lg mt-3">
            Start 30-Day Free Trial (No Card Needed)
          </a> 
          <div className="space-40"></div>

          <div className="btn2_group">
            <a id="cta_try_adcast_ios" target="_blank" href={getAppDownloadUrl('ios', activeLang)} onClick={(event) => { event.currentTarget.href = getAppDownloadUrl('ios', activeLang, getAffiliateCodeFromCurrentUrl()); }} aria-label="Download AdCast on App Store" description={'App store clicked'}><img src="/assets/icons/app_store.svg" alt="Download AdCast from App Store" loading="lazy" /></a> &nbsp;
            <a id="cta_try_adcast_android" target="_blank" href={getAppDownloadUrl('android', activeLang)} onClick={(event) => { event.currentTarget.href = getAppDownloadUrl('android', activeLang, getAffiliateCodeFromCurrentUrl()); }} aria-label="Get AdCast on Google Play" description={'Play store clicked'}><img src="/assets/icons/play_store.svg" alt="Download AdCast from Play Store" loading="lazy" /></a> &nbsp;
            <a id="cta_try_adcast_player" target="_blank" href="https://play.google.com/store/apps/details?id=com.adcast.player" description={'Download AdCast Player for Android'}><img src="/assets/icons/AdCastPlayer.svg" alt="Download AdCast Player from Play Store" style={{ width: '85px', borderRadius: '8px' }} loading="lazy" /></a>
            <a href="#" className="btn4">Start free trail</a>
            <a className="play_btn btn5" href="https://www.youtube.com/watch?v=xcJtL7QggTI">Watch Video <i className="fas fa-play"></i></a>
          </div>
        </div>
      </div> */}



      

      <div className="about_area section-padding2" id="how_it_works">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 text-center m-auto" data-home-reveal>
              <div className="heading hbm">
                <h2>{t('how_adcast_works.title')}</h2>
                <p className="lead">{t('how_adcast_works.sub_title')}</p>
              </div>
            </div>
          </div>
          <div className="row justify-content-center how-steps">
            <div className="col-lg-4 how-step">
              <div className="single_about about_line" style={{ position: 'relative' }} data-home-reveal data-motion-delay="1">
                {/* <img
                    src={line1}
                    alt=""
                    style={{
                    position: 'absolute',
                    top: 20,
                    left: '0%',
                    zIndex: -1
                    }}
                /> */}
                <div className="single_about_icon">
                  <Image src="/assets/img/icon/work11.svg" alt={t('how_adcast_works.step_1.image_alt')} loading="lazy" width={80} height={80} sizes="80px" />
                </div>
                <div className="content_box">
                  <h3>1. {t('how_adcast_works.step_1.title')}</h3>
                  <p>
                    <Trans
                      i18nKey="home:how_adcast_works.step_1.description"
                      components={{
                        appLink: (
                          <a
                            href="https://play.google.com/store/apps/details?id=com.adcast.player"
                            target="_blank"
                            rel="noopener noreferrer"
                          />
                        ),
                        webLink: (
                          <a href="https://player.adcast.app" target="_blank" rel="noopener noreferrer" />
                        ),
                      }}
                    />
                  </p>
                </div>
              </div>
            </div>
            <div className="col-lg-4 how-step">
              <div className="single_about about_line about_line2" style={{ position: 'relative' }} data-home-reveal data-motion-delay="2">
                {/* <img
                    src={line1}
                    alt=""
                    style={{
                    position: 'absolute',
                    top: '-23px',
                    left: '70%',
                    transform: 'rotate(180deg)',
                    zIndex: -1
                    }}
                /> */}
                <div className="single_about_icon">
                  <Image src="/assets/img/icon/work12.svg" alt={t('how_adcast_works.step_2.image_alt')} loading="lazy" width={80} height={80} sizes="80px" />
                </div>
                <div className="content_box">
                  <h3>2. {t('how_adcast_works.step_2.title')}</h3>
                  <p>{t('how_adcast_works.step_2.description')}</p>
                </div>
              </div>
            </div>
            <div className="col-lg-4 how-step">
              <div className="single_about" data-home-reveal data-motion-delay="3">
                <div className="single_about_icon">
                  <Image src="/assets/img/icon/work13.svg" alt={t('how_adcast_works.step_3.image_alt')} loading="lazy" width={80} height={80} sizes="80px" />
                </div>
                <div className="content_box">
                  <h3>3. {t('how_adcast_works.step_3.title')}</h3>
                  <p>{t('how_adcast_works.step_3.description')}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="adcast-how-video" data-home-reveal>
            <div className="adcast-how-video-frame">
              {videoRequested ? (
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/xaObKjed2sg${videoRequested ? '?autoplay=1' : ''}`}
                  title={t('how_adcast_works.title')}
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              ) : (
                <div className="adcast-how-video-preview">
                  <Image
                    src="/assets/img/home/adcast-phone-to-tv-digital-signage-demo.webp"
                    alt={t('privacy:video_preview_alt')}
                    fill
                    sizes="(max-width: 767px) 100vw, 920px"
                    quality={78}
                  />
                  <div className="adcast-how-video-preview-content">
                    <button type="button" className="adcast-how-video-play" onClick={() => setVideoRequested(true)}>
                      <span aria-hidden="true">▶</span>
                      {t('privacy:play_video')}
                    </button>
                    <button type="button" className="adcast-how-video-settings" onClick={openCookiePreferences}>
                      {t('privacy:manage_cookie_preferences')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
	    </div>

      <TVs />

      {/* ==== FEATURES SECTION ==== */}
      <div className="more_feature_area section-padding2 bg-light" id="main_features">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 text-center m-auto" data-home-reveal>
              <div className="heading hbm">
                <h2>{t('more_features.title')}</h2>
                <p>{t('more_features.sub_title')}</p>
              </div>
            </div>
          </div>

          <div className="row justify-content-center">
            {[
              { icon: "feature_1.svg", title: t('more_features.feature_1.title'), text: t('more_features.feature_1.description') },
              { icon: "feature_2.svg", title: t('more_features.feature_2.title'), text: t('more_features.feature_2.description') },
              { icon: "feature_3.svg", title: t('more_features.feature_3.title'), text: t('more_features.feature_3.description') },
              { icon: "feature_4.svg", title: t('more_features.feature_4.title'), text: t('more_features.feature_4.description') },
              { icon: "feature_5.svg", title: t('more_features.feature_5.title'), text: t('more_features.feature_5.description') },
              { icon: "feature_6.svg", title: t('more_features.feature_6.title'), text: t('more_features.feature_6.description') },
            ].map((f, i) => (
              <div className="col-lg-4" key={'feature_' + i}>
              <div className="single_more_feature" data-home-reveal data-motion-delay={(i % 3) + 1}>
                <div className="icon_box more_feature_icon" style={{ backgroundColor: '#111111'}}>
                  <Image src={`/assets/img/icon/${f.icon}`} alt={f.title} style={{ height: "70px", width: "auto" }} loading="lazy" width={70} height={70} sizes="70px" />
                </div>
                <div className="content_box more_feature_content">
                  <h3>{f.title}</h3>
                  <p>{f.text}</p>
                </div>
              </div>
            </div>
            ))}
          </div>
        </div>
      </div>


{/* ==== NEW SECTION 1: Who is it for? (targets long-tail) ==== */}
<div className="about_area section-padding2" id="who_is_it_for">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 text-center m-auto" data-home-reveal>
              <div className="heading hbm">
                    <h2>{t('perfect_for.title')}</h2>
                    <p className="lead">{t('perfect_for.description')}</p>
              </div>
            </div>
            <div className="adcast-use-case-grid" data-home-reveal data-motion-delay="1">
              {USE_CASE_CARDS.map((useCase) => {
                const title = t(`perfect_for.${useCase.key}.title`);
                const description = t(`perfect_for.${useCase.key}.description`);
                const titleContent = useCase.slug
                  ? <LangLink to={solSlug(useCase.slug)}>{title}</LangLink>
                  : title;

                return (
                  <article className="adcast-use-case-card" key={useCase.key}>
                    <div className="adcast-use-case-image">
                      <Image
                        src={`/assets/img/use-cases/${useCase.image}`}
                        alt={`${title}: ${description}`}
                        width={1200}
                        height={800}
                        sizes="(max-width: 575px) 100vw, (max-width: 991px) 50vw, 33vw"
                        loading="lazy"
                      />
                    </div>
                    <div className="adcast-use-case-content">
                      <Image
                        src={`/assets/img/icon/${useCase.icon}`}
                        alt=""
                        aria-hidden="true"
                        width={48}
                        height={48}
                        className="adcast-use-case-icon"
                      />
                      <h3 className="perfect-for-title">{titleContent}</h3>
                      <p>{description}</p>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </div>
</div>

{/* ==== NEW SECTION 2: Why phone-first beats everything else ==== */}
{/* <div className="section-padding bg-light">
  <div className="container">
    <div className="row align-items-center">
      <div className="col-lg-8">
        <div className="heading hbm">
          <h2>{t('mobile_first_section.title')}</h2>
          <p className="lead">{t('mobile_first_section.sub_title')}</p>
        </div>
        <ul className="list-check fa-ul mt-4">
          <li><i className="fa-li fas fa-check text-primary"></i> {t('mobile_first_section.point_1')}</li>
          <li><i className="fa-li fas fa-check text-primary"></i> {t('mobile_first_section.point_2')}</li>
          <li><i className="fa-li fas fa-check text-primary"></i> {t('mobile_first_section.point_3')}</li>
          <li><i className="fa-li fas fa-check text-primary"></i> {t('mobile_first_section.point_4')}</li>
        </ul>
      </div>
      <div className="col-lg-4 text-center">
        <img src="/assets/img/phone-editing.jpg" style={{ width: '250px'}} alt="Creating digital signage ad on phone" className="img-fluid rounded shadow" loading="lazy" srcSet="/assets/img/phone-editing-sm.jpg 300w, /assets/img/phone-editing.jpg 591w" sizes="250px" />
      </div>
    </div>
  </div>
</div> */}

{/* ==== Comparison table ==== */}
<section className="adcast-comparison-section section-padding2" aria-labelledby="adcast-comparison-title">
  <div className="container">
    <div className="heading hbm text-center" data-home-reveal>
      <h2 id="adcast-comparison-title">{t('adcast_vs.title')}</h2>
    </div>

    <div className="adcast-compare-table" role="table" data-home-reveal data-motion-delay="1">
      <div className="adcast-compare-row adcast-compare-header" role="row">
        <div className="adcast-compare-feature" role="columnheader"><span className="visually-hidden">{t('adcast_vs.title')}</span></div>
        <div className="adcast-compare-adcast" role="columnheader">
          <span className="adcast-compare-recommended" aria-label={t('adcast_vs.recommended')}>★ {t('adcast_vs.recommended')}</span>
          <Image src="/assets/adcast-original.svg" alt="AdCast" width={166} height={32} sizes="166px" className="adcast-compare-logo" />
          <span className="adcast-compare-tagline">{t('adcast_vs.tagline')}</span>
        </div>
        <div className="adcast-compare-traditional" role="columnheader">
          <Image src="/assets/img/icon/comparison-monitor.svg" alt="" aria-hidden="true" width={28} height={28} />
          <span>{t('adcast_vs.traditional_signage')}</span>
          <span className="adcast-compare-traditional-tagline">{t('adcast_vs.traditional_tagline')}</span>
        </div>
      </div>

      {COMPARISON_ROWS.map(({ key, icon }) => (
        <div className="adcast-compare-row" role="row" key={key}>
          <div className="adcast-compare-feature" role="rowheader">
            <Image src={`/assets/img/icon/${icon}`} alt="" aria-hidden="true" width={24} height={24} />
            <span className="adcast-compare-feature-copy">
              <strong>{t(`adcast_vs.comparison_${key}.feature`)}</strong>
              <span>{t(`adcast_vs.comparison_${key}.description`)}</span>
            </span>
          </div>
          <div className="adcast-compare-adcast" role="cell">
            <span className="adcast-compare-mobile-label">{t('adcast_vs.adcast_solution')}</span>
            <Image src="/assets/img/icon/comparison-check.svg" alt="" aria-hidden="true" width={24} height={24} />
            <strong>{t(`adcast_vs.comparison_${key}.adcast`)}</strong>
          </div>
          <div className="adcast-compare-traditional" role="cell">
            <span className="adcast-compare-mobile-label">{t('adcast_vs.traditional_signage')}</span>
            <Image src="/assets/img/icon/comparison-x.svg" alt="" aria-hidden="true" width={24} height={24} />
            <span>{t(`adcast_vs.comparison_${key}.traditional`)}</span>
          </div>
        </div>
      ))}
    </div>
    <div className="adcast-compare-cta" data-home-reveal data-motion-delay="2">
      <p className="adcast-compare-cta-copy">{t('adcast_vs.cta')}</p>
      <div className="adcast-compare-store-links">
        <a href={getAppDownloadUrl('ios', activeLang)} onClick={(event) => { event.currentTarget.href = getAppDownloadUrl('ios', activeLang, getAffiliateCodeFromCurrentUrl()); }} target="_blank" rel="noopener noreferrer" aria-label={t('call_to_action:labels.app_store_aria')}>
          <img src="/assets/icons/app_store.svg" alt={t('call_to_action:labels.app_store_alt')} width="160" height="58" loading="lazy" />
        </a>
        <a href={getAppDownloadUrl('android', activeLang)} onClick={(event) => { event.currentTarget.href = getAppDownloadUrl('android', activeLang, getAffiliateCodeFromCurrentUrl()); }} target="_blank" rel="noopener noreferrer" aria-label={t('call_to_action:labels.google_play_aria')}>
          <img src="/assets/icons/play_store.svg" alt={t('call_to_action:labels.google_play_alt')} width="160" height="58" loading="lazy" />
        </a>
      </div>
    </div>
    <Image src="/assets/img/icon/comparison-doodle-rays.svg" alt="" aria-hidden="true" width={62} height={55} className="adcast-compare-doodle adcast-compare-doodle-rays" />
    <Image src="/assets/img/icon/comparison-doodle-small-business.svg" alt="" aria-hidden="true" width={206} height={106} className="adcast-compare-doodle adcast-compare-doodle-small-business" />
  </div>
</section>

{/* ==== FAQ ==== */}
<FAQAccordion />







      {/* <div className="more_feature_area section-padding0 " id="main_features">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 text-center m-auto">
              <div className="heading hbm">
                <h2>{t('more_features.title')}</h2>
                <p>{t('more_features.sub_title')}</p>
              </div>
            </div>
          </div>

          <div className="row justify-content-center">
            {[
              { icon: "feature_1.png", title: t('more_features.feature_1.title'), text: t('more_features.feature_1.description') },
              { icon: "feature_2.png", title: t('more_features.feature_2.title'), text: t('more_features.feature_2.description') },
              { icon: "feature_3.png", title: t('more_features.feature_3.title'), text: t('more_features.feature_3.description') },
              { icon: "feature_4.png", title: t('more_features.feature_4.title'), text: t('more_features.feature_4.description') },
              { icon: "feature_5.png", title: t('more_features.feature_5.title'), text: t('more_features.feature_5.description') },
              { icon: "feature_6.png", title: t('more_features.feature_6.title'), text: t('more_features.feature_6.description') },
            ].map((f, i) => (
              <div className="col-lg-4" key={'feature_' + i}>
              <div className="single_more_feature">
                <div className="icon_box more_feature_icon" style={{ backgroundColor: '#111111'}}>
                  <img src={`/assets/img/icon/${f.icon}`} alt={f.title} style={{ height: "70px" }} loading="lazy" />
                </div>
                <div className="content_box more_feature_content">
                  <h3>{f.title}</h3>
                  <p>{f.text}</p>
                </div>
              </div>
            </div>
            ))}
          </div>
        </div>
      </div> */}

      {/* <!--::::: TESTIMONIAL AREA START :::::::--> */}
      <div className="testimonial_area section-padding2 third_bg">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 text-center m-auto" data-home-reveal>
              <div className="heading hbm white">
                <h2>{t('testimonial_area.title')}</h2>
                <p>{t('testimonial_area.sub_title')}</p>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-12 col-md-6">
              <div className="testimonials owl-carousel">
                <div className="single_testimonial" data-home-reveal data-motion-delay="1">
                  <p>{t('testimonial_area.review_1.text')}</p>
                  <div className="testi_author">
                    <div className="testi_author_img">
                      <Image src="/assets/img/review/PghDoorGuy.jpg" alt="K&B Garage Doors testimonial" loading="lazy" width={60} height={60} sizes="60px" />
                    </div>
                    <div className="rating">
                      <ul>
                        {Array.from({ length: 5 }, (_, index) => <li key={index} aria-label="5 star rating">★</li>)}
                      </ul>
                    </div>
                    <a href="https://www.kbgaragedoors.com" target="_blank" rel="noopener noreferrer">{t('testimonial_area.review_1.author')}</a>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-md-6 mt-4 mt-md-0">
              <div className="testimonials owl-carousel">
                <div className="single_testimonial" data-home-reveal data-motion-delay="2">
                  <p>{t('testimonial_area.review_2.text')}</p>
                  <div className="testi_author">
                    <div className="testi_author_img">
                      <Image src="/assets/img/review/DogmaVet.jpg" alt="Dogma Vet Clinic review" loading="lazy" width={60} height={60} sizes="60px" />
                    </div>
                    <div className="rating">
                      <ul>
                        {Array.from({ length: 5 }, (_, index) => <li key={index} aria-label="5 star rating">★</li>)}
                      </ul>
                    </div>
                    <a href="https://www.instagram.com/dogma.vet">{t('testimonial_area.review_2.author')}</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* <!--::::: TESTIMONIAL AREA END :::::::--> */}

    
      <CTA_2 />

      <section className="download-cta-mini section-padding2" id="download">
        <div className="container" data-home-reveal>
          <div className="download-cta-mini__card">
            <div className="download-cta-mini__copy">
              <span className="download-cta-mini__eyebrow">{t('download_area.eyebrow')}</span>
              <h2>{t('download_area.title')}</h2>
              <p>{t('download_cta_mini.description')}</p>
            </div>
            <LangLink to="/download" className="download-cta-mini__button">
              <span className="download-cta-mini__icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M12 4v11m0 0 4-4m-4 4-4-4M5 19h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <span>
                <small>{t('download_cta_mini.label')}</small>
                <strong>{t('download_cta_mini.cta')}</strong>
              </span>
              <b aria-hidden="true">→</b>
            </LangLink>
          </div>
        </div>
      </section>

        </PageLayout>
      </>
    );
}

export default Home;
